import { isQuestion, getRecentlyViewedCompanies } from '../utils/searchUtils';
import { companySearchCache, contentSearchCache } from './CacheService';
import { apiPost } from './apiClient';

class SearchService {
    constructor(flaskServerAddress, serverAddress) {
        this.flaskServerAddress = flaskServerAddress;
        this.serverAddress = serverAddress;
        this.hybridPath = `${flaskServerAddress}hybrid-search`;
    }

    async search(query, options = {}) {
        const { 
            startDate = null, 
            endDate = null,
            userId = null,
            shouldLogSearch = true
        } = options;

        if (!query) return null;

        // Create cache key based on query and options
        const cacheKey = this._createCacheKey(query, startDate, endDate);
        const cachedResults = contentSearchCache.get(cacheKey);
        
        if (cachedResults) {
            console.log('Using cached search results for:', query);
            return cachedResults;
        }

        try {
            let results;
            if (isQuestion(query)) {
                results = await this.handleQuestionSearch(query);
            } else {
                results = await this.handleRegularSearch(query, startDate, endDate);
            }
            
            // Cache the results if valid
            if (results) {
                contentSearchCache.set(cacheKey, results);
            }
            
            return results;
        } finally {
            if (shouldLogSearch) {
                await this.logSearch(query);
            }
        }
    }

    async handleQuestionSearch(query) {
        const encodedQuery = encodeURIComponent(query);
        const response = await fetch(`${this.flaskServerAddress}answer-question/${encodedQuery}/`);
        const data = await response.json();
        
        if (data.status === 'success') {
            return {
                type: 'question',
                answer: data.answer,
                posts: data.posts_used,
                noResults: false
            };
        }
        return null;
    }

    async handleRegularSearch(query, startDate, endDate) {
        const encodedQuery = encodeURIComponent(query);
        const start = startDate ? startDate.valueOf() / 1000 : 0;
        const end = endDate ? endDate.valueOf() / 1000 : 0;
        
        const url = `${this.hybridPath}/${encodedQuery}${start || end ? `/${start}/${end}` : ''}`;
        
        const response = await fetch(url);
        const data = await response.json();
        
        return {
            type: 'regular',
            posts: data,
            noResults: data.length === 0
        };
    }

    async searchCompanies(query) {
        if (!query || query.length < 2) return [];

        const cacheKey = query.toLowerCase().trim();
        const cachedResults = companySearchCache.get(cacheKey);

        if (cachedResults) {
            console.log('Using cached company results for:', query);
            return cachedResults;
        }

        // Check recently viewed companies that match the query
        const recentCompanies = getRecentlyViewedCompanies();
        const matchingRecentCompanies = recentCompanies.filter(company =>
            company.name && company.name.toLowerCase().includes(cacheKey)
        );

        try {
            const response = await apiPost('companies/search', { search_term: query });

            if (!response.ok) throw new Error('Company search failed');

            const data = await response.json();

            // Combine recent companies with search results
            let results = data || [];

            // Add recent matches at the top if they're not already in results
            if (matchingRecentCompanies.length) {
                const existingIds = new Set(results.map(c => c.id));
                const uniqueRecentMatches = matchingRecentCompanies.filter(c => !existingIds.has(c.id));

                // Combine and limit to reasonable size
                results = [...uniqueRecentMatches, ...results].slice(0, 10);
            }

            // Cache successful results
            companySearchCache.set(cacheKey, results);

            return results;
        } catch (error) {
            console.error('Company search error:', error);

            // Return matching recent companies if server request fails
            if (matchingRecentCompanies.length) {
                return matchingRecentCompanies;
            }
            return [];
        }
    }

    async logSearch(query) {
        try {
            // userId is now extracted from JWT token on the backend
            const response = await apiPost('log-search/', { query: query || '' });

            if (response.status !== 200) {
                console.error('Error logging search:', response);
            }
        } catch (error) {
            console.error('Error logging search:', error);
        }
    }
    
    _createCacheKey(query, startDate, endDate) {
        return `${query.toLowerCase().trim()}:${startDate || 0}:${endDate || 0}`;
    }
}

export default SearchService; 